import { apiClient } from "./auth";
import type { OverviewExportDeliveryMode } from "./overviewExportTypes";

export type OpportunityExportColumnGroup =
  | "OPPORTUNITY"
  | "PATIENT_CONTACT"
  | "ASSIGNMENT"
  | "CLINICAL"
  | "CALL_ATTEMPTS"
  | "CALL_FORM";

export type OpportunityExportStatus =
  "QUEUED" | "PROCESSING" | "COMPLETED" | "FAILED";

export type OpportunityExportConnectivityStatus =
  "RNR" | "DND" | "CALL_BACK_LATER" | "TRUE_CONNECT";

export interface OpportunityExportFilters {
  dateFrom: string;
  dateTo: string;
  status?: string[];
  stages?: string[];
  assignees?: string[];
  primaryFacilityId?: string[];
  doctorId?: string[];
  departmentId?: string[];
  type?: string[];
  disposition?: string[];
  admissionType?: string[];
  connectivityStatus?: OpportunityExportConnectivityStatus[];
  dndBucket?: boolean;
  leadSource?: string[];
  sourceChannel?: string[];
  payerType?: string[];
  /** CAR-2741 — Call Attempted buckets (`0` | `1` | `2` | `3` | `4+`). */
  callAttempted?: string[];
  /** CAR-2892 — Admission Case Type (`SURGICAL` | `MEDICAL` | `OTHERS` | `NOT_SPECIFIED`). */
  caseType?: string[];
  /** CAR-2741 — REGISTERED | UNREGISTERED. */
  registrationStatus?: "REGISTERED" | "UNREGISTERED";
  subDisposition?: string[];
  utmSource?: string[];
  utmCampaign?: string[];
}

export interface RequestOpportunityExportPayload {
  filters: OpportunityExportFilters;
  selectedColumnIds: string[];
  email?: string;
  deliveryMode?: OverviewExportDeliveryMode;
}

export interface RequestOpportunityExportAllPayload {
  selectedColumnIds: string[];
  email?: string;
  deliveryMode?: OverviewExportDeliveryMode;
  status?: string[];
  stages?: string[];
  assignees?: string[];
  primaryFacilityId?: string[];
  doctorId?: string[];
  departmentId?: string[];
  type?: string[];
  disposition?: string[];
  admissionType?: string[];
  connectivityStatus?: OpportunityExportConnectivityStatus[];
  dndBucket?: boolean;
  leadSource?: string[];
  sourceChannel?: string[];
  payerType?: string[];
  callAttempted?: string[];
  caseType?: string[];
  registrationStatus?: "REGISTERED" | "UNREGISTERED";
  subDisposition?: string[];
  utmSource?: string[];
  utmCampaign?: string[];
}

export interface RequestOpportunityExportResponse {
  success: boolean;
  exportId?: string;
  deliveryMode?: OverviewExportDeliveryMode;
  message?: string;
  error?: string;
}

export interface OpportunityExportRecord {
  _id: string;
  status: OpportunityExportStatus;
  processedCount?: number;
  rowCount?: number;
  fileSize?: number;
  presignedUrl?: string;
  presignedUrlExpiresAt?: string;
  downloadAvailable?: boolean;
  error?: string;
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string;
}

export async function requestOpportunityExportApi(
  payload: RequestOpportunityExportPayload
): Promise<RequestOpportunityExportResponse> {
  const res = await apiClient.post<
    RequestOpportunityExportPayload,
    RequestOpportunityExportResponse
  >("/opportunities/export", payload);
  return res.data;
}

export async function requestOpportunityExportAllApi(
  payload: RequestOpportunityExportAllPayload
): Promise<RequestOpportunityExportResponse> {
  const res = await apiClient.post<
    RequestOpportunityExportAllPayload,
    RequestOpportunityExportResponse
  >("/opportunities/export/export-all", payload);
  return res.data;
}

export async function getOpportunityExportStatusApi(
  exportId: string
): Promise<{ success: boolean; export?: OpportunityExportRecord }> {
  const res = await apiClient.get<{
    success: boolean;
    export?: OpportunityExportRecord;
  }>(`/opportunities/export/${encodeURIComponent(exportId)}`);
  return res.data;
}

export async function getOpportunityExportHistoryApi(params?: {
  limit?: number;
  offset?: number;
}): Promise<{
  success: boolean;
  exports?: OpportunityExportRecord[];
  count?: number;
}> {
  const query = new URLSearchParams();
  if (params?.limit != null) query.append("limit", String(params.limit));
  if (params?.offset != null) query.append("offset", String(params.offset));
  const url = query.toString()
    ? `/opportunities/export/history?${query.toString()}`
    : "/opportunities/export/history";
  const res = await apiClient.get<{
    success: boolean;
    exports?: OpportunityExportRecord[];
    count?: number;
  }>(url);
  return res.data;
}
